import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { GradientButton } from '../../components/Buttons';
import BodyWrap from '../../components/BodyWrap';
import { IconLockPassword, IconUser } from '@tabler/icons-react';
import { TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { clearLocalStorage } from '../../utils/authUtils';
import { ErrorResponseData } from '../../types/Common';

function AccountSettings() {
  useEffect(() => {
    document.title = 'Account & Security | PromptSlate';
  }, []);
  const navigate = useNavigate();
  let [isLoading, setIsLoading] = useState<boolean>(false);
  let [passwordUpdateMessage, setPasswordUpdateMessage] = useState<string>('');

  const updateUserDetailsForm = useForm({
    initialValues: {
      name: localStorage.getItem('name') || '',
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return 'Enter your name';
        }
      },
    },
  });

  type UpdatePasswordFormValues = {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  };

  const updatePasswordForm = useForm<UpdatePasswordFormValues>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },

    validate: {
      currentPassword: (value) => {
        if (!value) {
          return 'Enter your current password';
        }
      },
      newPassword: (value) => {
        if (!value) {
          return 'Enter a new password';
        }
      },
      confirmNewPassword: (value, values) => {
        if (!value) {
          return 'Confirm new password';
        } else if (value !== values.newPassword) {
          return 'Passwords do not match';
        }
      },
    },
  });

  const handleUpdatePasswordSubmit = async (values: any) => {
    setPasswordUpdateMessage('');
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/auth/update-password`,
        {
          current_password: values.currentPassword,
          new_password: values.newPassword,
        },
        {
          headers: headers,
        }
      );
      updatePasswordForm.reset();
      setPasswordUpdateMessage('Password updated');
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const data = error.response?.data as ErrorResponseData;
        const message = data?.message ?? 'An unexpected error occurred.';
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        } else {
          setPasswordUpdateMessage(message);
        }
      } else {
        setPasswordUpdateMessage('An error occured. Please try again later');
      }
    }
  };

  return (
    <BodyWrap>
      <div className="w-full h-fit flex flex-col gap-8 items-center mt-8">
        <div className="max-w-[800px] w-full bg-white rounded-lg border">
          <form noValidate>
            <div className="text-lg flex flex-row items-center h-[60px] p-5 border-b">
              <IconUser className="mr-2" /> Account details
            </div>
            <div className="flex flex-col items-center">
              <TextInput
                className="mt-8 w-[350px]"
                size="md"
                placeholder="Enter your name"
                {...updateUserDetailsForm.getInputProps('name')}
              />
              <TextInput
                className="mt-8 w-[350px]"
                size="md"
                value={localStorage.getItem('email') ?? 'Error loading email'}
                disabled
              />
              <GradientButton className="mt-5 mb-5" type="submit">
                Update details
              </GradientButton>
            </div>
          </form>
        </div>
        <div className="max-w-[800px] w-full bg-white rounded-lg border">
          <form
            noValidate
            onSubmit={updatePasswordForm.onSubmit((values) => handleUpdatePasswordSubmit(values))}
          >
            <div className="text-lg flex flex-row items-center h-[60px] p-5 border-b">
              <IconLockPassword className="mr-2" /> Update password
            </div>
            <div className="flex flex-col items-center">
              <TextInput
                type="password"
                className="mt-8 w-[350px]"
                size="md"
                placeholder="Current password"
                {...updatePasswordForm.getInputProps('currentPassword')}
              />
              <TextInput
                type="password"
                className="mt-5 w-[350px]"
                size="md"
                placeholder="New password"
                {...updatePasswordForm.getInputProps('newPassword')}
              />
              <TextInput
                type="password"
                className="mt-5 w-[350px]"
                size="md"
                placeholder="Confirm new password"
                {...updatePasswordForm.getInputProps('confirmNewPassword')}
              />
              {passwordUpdateMessage}
              <GradientButton className="mt-5 mb-5" type="submit">
                Update password
              </GradientButton>
            </div>
          </form>
        </div>
      </div>
    </BodyWrap>
  );
}

export default AccountSettings;
